import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
export type { CollegeStrategicPillar } from "@/types/entities";
import { toast } from "sonner";

export function useCollegeStrategicPillars() {
  return useQuery({
    queryKey: ["college-strategic-pillars"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/college-strategic-pillars", {});
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateCollegeStrategicPillar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateCollegeStrategicPillarRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/college-strategic-pillars", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College strategic pillar created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateCollegeStrategicPillar(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateCollegeStrategicPillarRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/college-strategic-pillars/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College strategic pillar updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteCollegeStrategicPillar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/college-strategic-pillars/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College strategic pillar deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
